// src/services/invitationApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

export interface Invitation {
  invitationId: string;
  email: string;
  roleName: string;
  expiresAt: string;
  createdAt: string;
  invitedBy: {
    userId: number;
    displayName: string;
    email: string;
  };
  projectId: number | null;
  projectName: string | null;
  userType: string | null;
  company: string | null;
  daysUntilExpiration: number;
  isExpired: boolean;
}

export interface PendingInvitationsResponse {
  invitations: Invitation[];
  total: number;
  expiredCount: number;
}

export interface ResendInvitationOptions {
  extendExpiration?: boolean;
  newExpirationDate?: string;
}

export interface InvitationApiError {
  error: boolean;
  message: string;
  code?: string;
}

/**
 * Get pending invitations (system-wide or project-specific)
 * @param projectId Optional project ID to filter by
 * @returns Promise with pending invitations
 */
export async function getPendingInvitations(projectId?: number): Promise<PendingInvitationsResponse> {
  const url = projectId 
    ? `${API_BASE_URL}/api/projects/${projectId}/invitations/pending`
    : `${API_BASE_URL}/api/admin/invitations/pending`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || `Failed to get pending invitations: ${response.statusText}`,
      code: errorData.code,
    } as InvitationApiError;
  }

  return response.json();
}

/**
 * Resend invitation email
 * @param invitationId Invitation ID
 * @param options Optional resend options
 */
export async function resendInvitation(
  invitationId: string, 
  options?: ResendInvitationOptions
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/invitations/${invitationId}/resend`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(options || {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || `Failed to resend invitation: ${response.statusText}`,
      code: errorData.code,
    } as InvitationApiError;
  }
}

/**
 * Reset invitation expiration date
 * @param invitationId Invitation ID
 * @param expiresAt New expiration date (ISO 8601)
 */
export async function resetInvitationExpiration(
  invitationId: string, 
  expiresAt: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/invitations/${invitationId}/expiration`, {
    method: 'PATCH',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ expiresAt }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || `Failed to reset invitation expiration: ${response.statusText}`,
      code: errorData.code,
    } as InvitationApiError;
  }
}

